import React, { useEffect } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import { useDispatch } from 'react-redux';
import {
  Button,
  IconButton,
  Grid,
  Box,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Card,
  CardContent,
  Tooltip
} from '@material-ui/core';
import { Add, Delete, ZoomOutMap } from '@material-ui/icons';
import CloseIcon from '@material-ui/icons/Close';
import useRules from './useRules';
import { useState } from 'react';
import { useDebounce } from 'helpers';
import DefaultTemplate from './components/DefaultTemplate';
import { toTemplateDropdownOptions } from 'views/Campaign/helpers';
import {LastTemplateModalStyle,cleanStyle,throttle, addDragElementStyle, removeDragElementStyle, retainStyleForSeconds, dragOverStyle, scrollController} from 'views/Campaign/helpers/dragAndDropHelper'
import { constants } from 'views/Campaign';
import useTemplate from 'views/Template/useTemplate';
import { actions } from 'store/template';
import { Details, Design } from 'views/Template/components';
import { unsetCurrentTemplate, getTemplate } from 'store/template/actions';
import { QueryBuilder } from 'components';
import TemplateSingleSelect from 'components/TemplateSingleSelect';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
let draggedIndex = null;
let draggedId = null;
const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1
  },
  button: {
    margin: theme.spacing(1)
  },
  deleteButton: {
    color: theme.palette.error.main
  },
  openButton: {
    color: theme.palette.primary.main
  },
  containerMargin: {
    marginLeft: theme.spacing(3)
  },
  selectContainer: {
    display: 'inline',
    minWidth: '14rem',
    margin: theme.spacing(1)
  },
  autoCompleteContainer: {
    display: 'inline',
    minWidth: '14rem',
    margin: theme.spacing(1),
    marginTop: theme.spacing(2)
  },
  buttonContainer: {
    display: 'inline',
    margin: theme.spacing(1)
  },
  ruleError: {
    color: theme.palette.error.main,
    paddingTop: '13px'
  },
  dateField: {
    width: '100%',
    marginTop: '0px'
  },
  templateButtonError: {
    paddingLeft: '19px',
    color: theme.palette.error.main
  },
  ruleDivider: {
    marginTop: '10px',
    marginBottom: '10px'
  },
  dialogSubTitle: {
    fontSize: '14px',
    fontWeight: '400',
    color: '#546e7a',
    letterSpacing: '-0.05px'
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500]
  },
  nextButton: {
    marginRight: '16px',
    marginBottom: '8px'
  },
  backButton: {
    marginLeft: '16px',
    marginBottom: '8px'
  }
}));

const Rules = props => {
  const {
    className,
    campaign,
    templates,
    paginatedTemplates,
    templateFilters,
    isTemplateDropdownLoading,
    isTemplateNameHydrationLoading,
    templateSelectedNamesByUuid,
    templateTags,
    ...rest
  } = props;
  const {
    thisCampaign,
    attributes,
    handleChange,
    onBlur,
    onFocus,
    shouldShowError,
    getErrorMessage,
    isCampaignReadOnly,
    removeRuleBasedTemplate
  } = campaign;

  const {
    templateModels,
    addTemplateModel,
    deleteTemplateModel,
    updateTemplateModelProperty,
    deleteAndAdd
  } = useRules({ thisCampaign, handleChange });

  const { INTERNAL_SURVEY_RESPONSE, EXTERNAL_SURVEY_RESPONSE } = constants.campaignOccurrenceTypes;
  const hierarchyFilterOptions = [
    {
      value: 'global',
      searchScope: 'GLOBAL DEFAULT'
    },
    {
      value: 'tenant',
      searchScope: 'TENANT DEFAULT'
    },
    {
      value: 'property',
      searchScope: 'PROPERTY LEVEL'
    }
  ];
  const tagFilterOptions = (templateTags || []).map(tag => ({
    label: tag,
    value: tag
  }));

  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    unsetNextStep,
    getPaginatedTemplates,
    updateCommunicationFilters,
    getTemplateTags
  } = actions;
  const toOptions =
    typeof toTemplateDropdownOptions === 'function'
      ? toTemplateDropdownOptions
      : values => values;
  const availablePaginatedTemplates = Array.isArray(paginatedTemplates)
    ? paginatedTemplates
    : templates;
  const [open, setOpen] = React.useState(false);
  const template = useTemplate(undefined, undefined, open);
  const [isDraggingOver, setIsDraggingOver] = useState(null);
  const [isDraggingOverPosition, setIsDraggingOverPosition] = useState(null);
  const dragOverId = useDebounce(isDraggingOver, 500);
  const {
    thisTemplate,
    isLoading,
    isInTemplateDesignDialog,
    goNext,
    goBack
  } = template;

  useEffect(() => {
    campaign.setCurrentTemplateDetails();
  }, []);

  useEffect(() => {
    if (templateModels.length === 0) {
      removeRuleBasedTemplate();
    }
  }, [templateModels]);

  const openCreateTemplateDialog = () => {
    if (!thisTemplate.schemaIdentifier) {
      campaign.setCurrentTemplateDetails();
    }
    dispatch(getTemplate({}));
    setOpen(true);
  };

  const closeCreateTemplateDialog = () => {
    setOpen(false);
    template.resetValidationErrors();
    dispatch(unsetNextStep());
    dispatch(unsetCurrentTemplate());
  };

  const getTemplateOptions = (templateModel, selectedTemplateUuid) => {
    const templateOptions = toOptions(availablePaginatedTemplates).map(option => ({
      ...option,
      disabled: option?.active === false
    }));

    return {
      list: templateOptions,
      keyId: 'uuid',
      valueId: 'uuid',
      displayValueId: 'name'
    };
  };

  const fetchPaginatedTemplateOptions = overrides => {
    const campaignContext = {
      type: thisCampaign.type,
      productType: thisCampaign.productType,
      subscriberListId: thisCampaign.subscriberListId,
      segmentedListId: thisCampaign.segmentedListId,
      eventTypeCategory: thisCampaign.eventTypeCategory,
      promotional: thisCampaign.promotional,
      campaignOccurrenceType: thisCampaign.campaignOccurrenceType
    };

    dispatch(
      updateCommunicationFilters({
        filterTypes: thisCampaign.type ? [thisCampaign.type] : [],
        filterProductNames: thisCampaign.productName ? [thisCampaign.productName] : [],
        filterProductCategories: thisCampaign.productCategory
          ? [thisCampaign.productCategory]
          : [],
        ...overrides
      })
    );
    dispatch(getPaginatedTemplates({ force: true, campaignContext }));
  };

  const handleTemplateDropdownOpen = initialSearchInput => {
    if (!thisCampaign?.campaignOccurrenceType) {
      return;
    }

    if ((templateTags || []).length === 0) {
      dispatch(getTemplateTags());
    }

    fetchPaginatedTemplateOptions({
      pageNo: 0,
      searchInput: (initialSearchInput || '').trim(),
      filterTemplates: [],
      filterTags: [],
      filterStatus: []
    });
  };

  const handleTemplateSearchChange = searchInput => {
    fetchPaginatedTemplateOptions({ searchInput, pageNo: 0 });
  };

  const handleTemplatePageChange = pageNo => {
    fetchPaginatedTemplateOptions({ pageNo });
  };

  const handleTemplateRowsPerPageChange = rowsPerPage => {
    fetchPaginatedTemplateOptions({ rowsPerPage, pageNo: 0 });
  };

  const handleTemplateTagChange = filterTags => {
    fetchPaginatedTemplateOptions({ filterTags, pageNo: 0 });
  };

  const handleTemplateStatusChange = filterStatus => {
    fetchPaginatedTemplateOptions({ filterStatus, pageNo: 0 });
  };

  const handleTemplateHierarchyChange = filterTemplates => {
    fetchPaginatedTemplateOptions({ filterTemplates, pageNo: 0 });
  };

  const editTemplateModel = templateUuid => {
    dispatch(getTemplate({ templateUuid }))
    setOpen(true);
  }

  useEffect(() => {
    const nextElement = isDraggingOverPosition > 0 ? templateModels[isDraggingOverPosition - 1] : null;

   dragOverStyle(nextElement,dragOverId);
    return () => {
      if (dragOverId) {
        cleanStyle(dragOverId, nextElement);
      }
    };
  }, [dragOverId]);

  const handleDragStart = (e, index, id) => {
    addDragElementStyle(id);
    draggedIndex = index;
    draggedId = id;
  };

  function throttledDragOver(id, index) {
    if (index == draggedIndex || index == draggedIndex + 1) {
      return;
    }
    if (index > templateModels.length - 1) {
      setIsDraggingOverPosition(index);
      return;
    }
    setIsDraggingOver(id);
    setIsDraggingOverPosition(index);
  }

  const handleDragOver = (event, id, index) => {
    event.preventDefault();
    event.stopPropagation();
    scrollController(event);
    if (draggedIndex == null) return;
    throttle(throttledDragOver(id, index), 800);
  };

  const handleDrop = (e, index, id) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedIndex == null) return;
    if (id)
      cleanStyle(id)
    if (index == draggedIndex || index == draggedIndex + 1) {
      return;
    }
    deleteAndAdd(draggedIndex, index);
    
    setTimeout(() => {
      retainStyleForSeconds(draggedId);
      draggedId = null;
    }, 200);  

    setIsDraggingOver(null);
    setIsDraggingOverPosition(null);
    draggedIndex = null;
  };

  const handleDragEnd = () => {
    setIsDraggingOver(null);
    setIsDraggingOverPosition(null);
    draggedIndex = null;
    removeDragElementStyle(draggedId);
    draggedId = null;
  };

  const throttledDragLeave = (event) => {
    setIsDraggingOver(null);
    setIsDraggingOverPosition(null);
  };

  function handleDragLeave(event) {
    event.preventDefault();
    event.stopPropagation();
    throttle(throttledDragLeave(event), 800);
  }

  const getQuery = templateModel => {
    let filteredTemplates = thisCampaign.templates.filter(
      temp =>
        temp.communicationTemplateUuid ===
        templateModel.communicationTemplateUuid && temp.id === templateModel.id
    );

    return filteredTemplates?.length !== 0
      ? filteredTemplates[0]
      : {
        communicationTemplateUuid: templateModel.communicationTemplateUuid || '',
        id: templateModel.id
      };
  };

  return (
    <div {...rest} className={clsx(classes.root, className)}>
      {thisCampaign.campaignOccurrenceType !== INTERNAL_SURVEY_RESPONSE &&
        thisCampaign.campaignOccurrenceType !== EXTERNAL_SURVEY_RESPONSE &&
        !isCampaignReadOnly() && (
          <Button
            variant="contained"
            color="primary"
            disabled={
              templateModels.length > 0 &&
              templateModels[0].communicationTemplateUuid === ''
            }
            className={classes.button}
            startIcon={<Add />}
            onClick={addTemplateModel}>
            Add Rule Based Template
          </Button>
        )}

      {!isCampaignReadOnly() && (
        <Button
          variant="contained"
          color="primary"
          disabled={!(thisCampaign.communicationTemplateUuid === undefined)}
          className={classes.button}
          startIcon={<Add />}
          onClick={() =>
            handleChange({
              target: { value: '', name: 'communicationTemplateUuid' }
            })
          }>
          Add Default Template
        </Button>
      )}

      {!isCampaignReadOnly() && (
        <Button
          variant="contained"
          color="primary"
          className={classes.button}
          startIcon={<Add />}
          onClick={openCreateTemplateDialog}>
          Create New Template
        </Button>
      )}

      {!isInTemplateDesignDialog ? (
        <Dialog
          open={open}
          onClose={closeCreateTemplateDialog}
          aria-labelledby="form-dialog-title"
          fullWidth
          maxWidth="lg">
          <Typography variant="h2">
            <DialogTitle id="form-dialog-title" disableTypography={true}>
              <div>Template Details</div>
              <div className={classes.dialogSubTitle}>
                * indicates a required field
              </div>
            </DialogTitle>
            <IconButton
              aria-label="close"
              className={classes.closeButton}
              onClick={closeCreateTemplateDialog}>
              <CloseIcon />
            </IconButton>
          </Typography>
          <DialogContent>
            {isLoading && (
              <Box style={{ width: '50%' }}>
                {' '}
                <CircularProgress style={{ float: 'right' }} />
              </Box>
            )}
            {!isLoading && (
              <Details
                isPopupDialog={true}
                template={template}
                useCampaignAutomation={false}
                campaign={campaign}></Details>
            )}
          </DialogContent>

          <DialogActions>
            <Button
              className={classes.nextButton}
              onClick={goNext}
              disabled={isLoading}
              color="primary"
              variant="contained">
              Next
            </Button>
          </DialogActions>
        </Dialog>
      ) : (
        <Dialog
          variant="h2"
          open={open}
          onClose={closeCreateTemplateDialog}
          aria-labelledby="form-dialog-title"
          fullWidth
          maxWidth="lg">
          <Typography variant="h2">
            <DialogTitle id="form-dialog-title" disableTypography={true}>
              <div>Template Design</div>
              <div className={classes.dialogSubTitle}>
                * indicates a required field
              </div>
            </DialogTitle>
            <IconButton
              aria-label="close"
              className={classes.closeButton}
              onClick={closeCreateTemplateDialog}>
              <CloseIcon />
            </IconButton>
          </Typography>
          <DialogContent
            style={{ height: thisTemplate.type === 'EMAIL' && '670px' }}>
            <Design isPopupDialog={true} template={template}></Design>
          </DialogContent>
          <DialogActions>
            <Button
              className={classes.backButton}
              onClick={goBack}
              disabled={isLoading}
              color="secondary"
              variant="contained">
              Back
            </Button>
            <Box flexGrow={1}>&nbsp;</Box>
          </DialogActions>
        </Dialog>
      )}

      {shouldShowError(`communicationTemplateUuid`) && (
        <Typography
          className={classes.templateButtonError}
          variant="caption"
          display="block">
          {thisCampaign.campaignOccurrenceType !== INTERNAL_SURVEY_RESPONSE &&
            thisCampaign.campaignOccurrenceType !== EXTERNAL_SURVEY_RESPONSE
            ? '*Please add at least one rule based template or a default template.'
            : '*Please add at least one template.'}
        </Typography>
      )}

      <DefaultTemplate
        templates={templates}
        paginatedTemplates={paginatedTemplates}
        templateFilters={templateFilters}
        isTemplateDropdownLoading={isTemplateDropdownLoading}
        isTemplateNameHydrationLoading={isTemplateNameHydrationLoading}
        templateSelectedNamesByUuid={templateSelectedNamesByUuid}
        hierarchyFilterOptions={hierarchyFilterOptions}
        selectedHierarchyValues={templateFilters?.filterTemplates || []}
        selectedStatusValues={templateFilters?.filterStatus || []}
        tagFilterOptions={tagFilterOptions}
        selectedTagValues={templateFilters?.filterTags || []}
        onTemplateDropdownOpen={handleTemplateDropdownOpen}
        onTemplateHierarchyChange={handleTemplateHierarchyChange}
        onTemplateStatusChange={handleTemplateStatusChange}
        onTemplateTagChange={handleTemplateTagChange}
        onTemplateSearchChange={handleTemplateSearchChange}
        onTemplatePageChange={handleTemplatePageChange}
        onTemplateRowsPerPageChange={handleTemplateRowsPerPageChange}
        campaign={campaign}
        onEditTemplate={editTemplateModel}
      />

      {templateModels &&
        templateModels.map((templateModel, templateIndex) => (
          <>
            <div
              id={templateModel.id}
              onDragLeave={(event) => { handleDragLeave(event, templateIndex + 1) }}
              onDragOver={(event) => {
                handleDragOver(event, templateModel.id, templateIndex)
              }}
              onDrop={(event) => {
                handleDrop(event, templateIndex, templateModel.id);
              }}
              style={{ border: '5px dotted transparent' }}>
              <Box
                key={`Template_${templateModel.id}${templateIndex}`}>
                <Card style={{ marginTop: 7 }}>
                  <CardContent>
                    <Grid container>
                      <Grid item className={classes.selectContainer}>
                        <TemplateSingleSelect
                          fullWidth
                          label="Select Template"
                          margin="dense"
                          name="communicationTemplateUuid"
                          onChange={e =>
                            updateTemplateModelProperty(templateModel.id, e)
                          }
                          onFocus={onFocus}
                          onBlur={onBlur}
                          required
                          disabled={isCampaignReadOnly()}
                          value={templateModel.communicationTemplateUuid || ''}
                          selectedDisplayValue={
                            availablePaginatedTemplates.find(
                              templateItem =>
                                templateItem.uuid ===
                                templateModel.communicationTemplateUuid
                            )?.name ||
                            templateSelectedNamesByUuid?.[
                              templateModel.communicationTemplateUuid
                            ] ||
                            ''
                          }
                          error={shouldShowError(
                            `templates.${templateIndex}.communicationTemplateUuid`
                          )}
                          helperText={getErrorMessage(
                            `templates.${templateIndex}.communicationTemplateUuid`
                          )}
                          options={getTemplateOptions(
                            templateModel,
                            templateModel.communicationTemplateUuid
                          )}
                          remotePagination
                          totalCount={templateFilters?.totalCount || 0}
                          isLoading={isTemplateDropdownLoading}
                          showFieldSkeleton={isTemplateNameHydrationLoading}
                          hierarchyOptions={hierarchyFilterOptions}
                          selectedHierarchyValues={templateFilters?.filterTemplates || []}
                          selectedStatusValues={templateFilters?.filterStatus || []}
                          tagOptions={tagFilterOptions}
                          selectedTagValues={templateFilters?.filterTags || []}
                          onOpen={handleTemplateDropdownOpen}
                          onHierarchyChange={handleTemplateHierarchyChange}
                          onStatusChange={handleTemplateStatusChange}
                          onTagChange={handleTemplateTagChange}
                          onSearchChange={handleTemplateSearchChange}
                          onPageChange={handleTemplatePageChange}
                          onRowsPerPageChange={handleTemplateRowsPerPageChange}
                          emptyStateText="No templates found"
                          dataTestId="select-communicationTemplateUuid"
                        />
                      </Grid>
                      {templateModel.communicationTemplateUuid && (
                        <Grid item className={classes.buttonContainer}>
                          <Tooltip title="Open">
                            <IconButton
                              className={classes.openButton}
                              onClick={() => editTemplateModel(templateModel.communicationTemplateUuid)}>
                              <ZoomOutMap />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      )}
                      {!isCampaignReadOnly() && (
                        <>
                          <Grid item className={classes.buttonContainer}>
                            <Tooltip title="Delete">
                              <IconButton
                                className={classes.deleteButton}
                                onClick={() => deleteTemplateModel(templateModel.id)}>
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </Grid>
                          <Grid item className={classes.buttonContainer} style={{ paddingTop: '9px' }} draggable={true}
                            onDragStart={(event) => { event.stopPropagation(); handleDragStart(event, templateIndex, templateModel.id) }}
                            onDragEnd={(e) => { e.stopPropagation(); handleDragEnd() }} >
                            <Tooltip title="Drag">
                              <IconButton
                                className={classes.removeButton}
                                size="small"
                                style={{ cursor: 'move' }}
                                title='Drag to reorder'
                              >
                                <DragIndicatorIcon style={{ color: '#43a047' }} />
                              </IconButton>
                            </Tooltip>
                          </Grid></>
                      )}
                      <Grid item className={classes.ruleError}></Grid>
                    </Grid>
                    <Grid container style={{ marginTop: '8px' }}>
                      {thisCampaign.templates && (
                        <>
                          <QueryBuilder
                            name={`templates.${templateIndex}`}
                            isLoading={isLoading}
                            query={getQuery(templateModel)}
                            template={{ thisTemplate: thisCampaign, attributes, templateId: templateModel.id }}
                            onChange={handleChange}
                            isReadOnly={isCampaignReadOnly}
                          />
                          {thisCampaign?.templates?.length > 0 &&
                            templateModel.communicationTemplateUuid &&
                            (shouldShowError(
                              `templates.${templateIndex}.subConditionExpressions`
                            ) ||
                              shouldShowError(
                                `templates.${templateIndex}.valid`
                              )) && (
                              <Typography
                                className={classes.templateButtonError}
                                variant="caption"
                                display="block">
                                {getErrorMessage(
                                  `templates.${templateIndex}.subConditionExpressions`
                                ) || '*Please enter a complete valid expression.'}
                              </Typography>
                            )}
                        </>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Box></div>
            {templateIndex == templateModels.length - 1 &&
              <div
                onDragOver={(event) => {
                  handleDragOver(event, templateModel.id, templateIndex + 1)
                }}
                onDragLeave={(event) => { handleDragLeave(event, templateIndex + 1) }}
                onDrop={(event) => {
                  handleDrop(event, templateIndex + 1, templateModel.id);
                }}
              >
                <LastTemplateModalStyle isOverLast={isDraggingOverPosition == templateIndex + 1} />
              </div>}</>

        ))}
    </div>
  );
};

export default Rules;
